/*
Adopción del token renovado que manda la API.

Por qué existe. El JWT dura 12h y no deslizaba. Al vencer había dos caminos y los dos
eran malos: el modal de "tu sesión va a expirar" —que arma un setTimeout AL MONTAR para
exp - 60s, así que un equipo suspendido o una pestaña en segundo plano se lo pierde
entero— y el silencio de las conexiones SSE, que usan EventSource, no pasan por
customFetch y por lo tanto nunca avisan que la sesión murió. El usuario se enteraba
recién al apretar algo: "estaba publicando y me dijo User not found" (Helpcar,
2026-08-25).

Ahora la API devuelve un token nuevo en un header cuando al vigente le queda poco (ver
api: utils/tokenRefresh.ts). Acá sólo hay que guardarlo. No hay timer que se pierda por
una suspensión, no hay ventana de 60 segundos que haya que acertar, y el usuario no
tiene que apretar nada.

El header lo revisan los dos clientes HTTP —customFetch y axios—, igual que la marca de
sesión vencida.
*/

/** Header con el que la API devuelve un JWT recién emitido. */
export const TOKEN_REFRESH_HEADER = 'X-Veekls-Token'

/**
 * Aviso de que hay token nuevo, para que AuthProvider actualice su estado.
 *
 * No alcanza con escribir en localStorage: las peticiones lo leen de ahí y andarían
 * bien, pero `useTokenExpiration` arma el temporizador de cierre a partir del estado de
 * React. Si React se queda con el token viejo, ese temporizador dispara igual sobre el
 * `exp` anterior y cierra la sesión de alguien que tiene un token perfectamente válido.
 */
export const TOKEN_REFRESHED_EVENT = 'veekls:token-refreshed'

/**
 * Vencimiento (`exp`, en segundos) de un JWT, sin verificar la firma: acá sólo hace
 * falta compararlo con el que ya tenemos. Devuelve null si no se puede leer.
 */
export const readTokenExpiry = (token: string): number | null => {
  try {
    const [, payload] = token.split('.')

    if (!payload) return null

    // El payload viene en base64url; atob sólo entiende base64 clásico con relleno.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const { exp } = JSON.parse(atob(padded)) as { exp?: unknown }

    return typeof exp === 'number' && Number.isFinite(exp) ? exp : null
  } catch {
    return null
  }
}

/*
Un token que llega en el header no es necesariamente NUEVO.

El 2026-09-04 un usuario de Olea Flaño quedaba fuera de la aplicación cada vez que
entraba a notas de venta, sin que la API rechazara nada. La API manda ETag y el
navegador guarda las respuestas; cuando el listado no cambia responde 304 y el
navegador entrega la copia guardada CON SUS HEADERS. Esa copia, del 2026-09-01, traía
un X-Veekls-Token de aquel día. Acá lo adoptábamos como si fuera una renovación, y
cuando ese token venció, useTokenExpiration cerró la sesión en el acto. Mientras seguía
vigente no se notaba, salvo porque la API lo volvía a renovar una y otra vez.

Por eso un token del header sólo se adopta si vence DESPUÉS del que ya tenemos y todavía
no venció. Una renovación legítima siempre cumple las dos cosas: nace con la vida
completa, más larga que la del token que la provocó.
*/
export const isNewerToken = (renewed: string, current: string, now: number = Date.now()): boolean => {
  const renewedExpiry = readTokenExpiry(renewed)

  if (renewedExpiry === null) return false

  if (renewedExpiry * 1000 <= now) return false

  const currentExpiry = readTokenExpiry(current)

  // Si el actual no se puede leer, la sesión ya está rota; el nuevo, vigente, la arregla.
  if (currentExpiry === null) return true

  return renewedExpiry > currentExpiry
}

/**
 * Guarda el token renovado, si vino uno y es realmente más nuevo que el actual.
 *
 * No hace nada si no hay token guardado: eso significa que la sesión ya se cerró, y una
 * respuesta en vuelo no puede volver a abrirla.
 */
export const applyRefreshedToken = (renewed: string | null | undefined): void => {
  if (!renewed) return

  const current = localStorage.getItem('authToken')

  if (!current || current === 'null' || current === '') return

  if (current === renewed) return

  if (!isNewerToken(renewed, current)) return

  localStorage.setItem('authToken', renewed)
  window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: renewed }))
}

/** Lee el header de una respuesta de fetch. Headers.get es case-insensitive. */
export const readRefreshedToken = (response: Response): string | null => response.headers.get(TOKEN_REFRESH_HEADER)
